import PropTypes from 'prop-types';
import { Box, Divider, Skeleton, Stack, styled } from '@mui/material';
import React from 'react';

const ListItem = styled(Stack)(({ theme }) => ({
  '&': {
    lineHeight: 'normal',
  },
  '& .name, .value': {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '152%',
  },
  '& .name': {
    color: '#7A7B84',
  },
  '& .value': {
    color: '#111111',
    verticalAlign: 'text-bottom',
    position: 'relative',
  },
  '& .value > *': {
    display: 'flex',
    alignItems: 'end',
  },
}));

export const KeyValueListItem = ({ name, value, children, loading }) => {
  const resolvedValue = typeof value === 'function' ? value() : value ?? children;

  return (
    <ListItem direction="row" spacing={0} alignItems="baseline">
      {loading ? (
        <Skeleton width="80px" variant="rectangular">
          <Box sx={{ height: '20px' }} />
        </Skeleton>
      ) : (
        <Box className="name">{name}</Box>
      )}

      <Box sx={{ flex: 1, px: 1 }}>
        <Divider sx={{ backgroundColor: '#ececec', height: '1px' }} />
      </Box>

      {loading ? (
        <Skeleton width="80px" variant="rectangular">
          <Box sx={{ height: '20px' }} />
        </Skeleton>
      ) : (
        <Box className="value">{resolvedValue}</Box>
      )}
    </ListItem>
  );
};

KeyValueListItem.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  children: PropTypes.node,
  loading: PropTypes.bool,
};

const KeyValueList = ({ list, children, loading = false, ...props }) => {
  const hasChildren = Boolean(children);
  const hasList = Array.isArray(list) && list.length > 0;

  if (!hasChildren && !hasList) {
    return null;
  }

  return (
    <Stack spacing={2} {...props}>
      {hasList &&
        list.map(({ name, value }) => <KeyValueListItem key={name} name={name} value={value} loading={loading} />)}

      {hasChildren &&
        React.Children.map(children, (child) =>
          React.isValidElement(child) ? React.cloneElement(child, { loading }) : child
        )}
    </Stack>
  );
};

KeyValueList.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
    })
  ),
  children: PropTypes.node,
  loading: PropTypes.bool,
};

// Compound component API
KeyValueList.Item = KeyValueListItem;

export default KeyValueList;
